/**
 * The recorded conversation.
 *
 * The published demo is a walkthrough, not a live agent. All five turns were
 * asked against the live dev system and their SSE streams captured; the answer
 * text, structured cards, follow-up chips, retrieved sources and timings in
 * recordedTurns.generated.ts are the product's own output, embedded verbatim.
 * Nothing here is written for the demo.
 *
 * Seeded straight into the thread store at boot (see installDemoApi), because
 * the composer is disabled and no turn can be produced by typing.
 */

import { DEMO_PATIENT_FHIR_ID, DEMO_PATIENT_NAME } from './patient';
import { GENERATED_TURNS, type GeneratedTurn } from './recordedTurns.generated';

export type RecordedTurn = GeneratedTurn;

/** In the order they were asked. */
export const RECORDED_TURNS: RecordedTurn[] = GENERATED_TURNS;

// ── seeding the thread store ────────────────────────────────────────────────

const THREAD_ID = 'demo-recorded-thread';
/** services/threadService.ts — sessionStorage, SCHEMA_VERSION 2, and with IAM
 *  disabled there is no user scope, so the key is the bare one. */
const THREAD_STORAGE_KEY = 'clinician_threads';
const SCHEMA_VERSION = 2;

/** Timestamps are fixed so the transcript reads the same on every run. */
const BASE_TS = Date.parse('2026-08-22T09:14:00Z');

function storedMessages() {
  const out: Array<Record<string, unknown>> = [];
  let clock = BASE_TS;
  RECORDED_TURNS.forEach((turn) => {
    const elapsed = turn.processing_time_ms ?? 4000;
    const askedAt = new Date(clock).toISOString();
    const answeredAt = new Date(clock + elapsed).toISOString();
    // Space the turns out the way a real session would: each question is asked
    // a little after the previous answer landed.
    clock += elapsed + 45_000;

    out.push({
      id: `${turn.id}-user`,
      role: 'user',
      content: turn.question,
      timestamp: askedAt,
    });
    out.push({
      id: `${turn.id}-assistant`,
      role: 'assistant',
      answer: turn.answer,
      thinkingComplete: true,
      isStreaming: false,
      streamPhase: 'final',
      responseMetadata: {
        conversation_id: THREAD_ID,
        intent_type: turn.intent_type ?? undefined,
        tools_used: turn.tools_used,
        processing_time_ms: elapsed,
        client_processing_time_ms: elapsed,
        authoritative_final_text: turn.answer,
        suggested_next_questions: turn.suggestions,
        ...(turn.structured ? { structured: turn.structured } : {}),
        ...(turn.rag_sources ? { rag_sources: turn.rag_sources } : {}),
        ...(turn.guideline_evidence ? { guideline_evidence: turn.guideline_evidence } : {}),
        ...(turn.specialty_outputs ? { specialty_outputs: turn.specialty_outputs } : {}),
        ...(turn.engaged_specialties ? { engaged_specialties: turn.engaged_specialties } : {}),
        ...(turn.router_overview ? { router_overview: turn.router_overview } : {}),
      },
      timestamp: answeredAt,
    });
  });
  return out;
}

/**
 * Write the recorded conversation into the thread store, unless something is
 * already there — a reload must not duplicate it, and a real session (if this
 * build were ever run against a backend) must not be overwritten.
 */
export function seedRecordedTranscript(): void {
  try {
    const store = window.sessionStorage;
    const existing = store.getItem(THREAD_STORAGE_KEY);
    if (existing && existing.includes(THREAD_ID)) return;
    store.setItem(
      THREAD_STORAGE_KEY,
      JSON.stringify({
        version: SCHEMA_VERSION,
        currentThreadId: THREAD_ID,
        threads: [
          {
            id: THREAD_ID,
            title: RECORDED_TURNS[0]?.question ?? 'Recorded session',
            timestamp: new Date(BASE_TS).toISOString(),
            patientId: DEMO_PATIENT_FHIR_ID,
            patientName: DEMO_PATIENT_NAME,
          },
        ],
        messagesByThread: { [THREAD_ID]: storedMessages() },
      }),
    );
  } catch {
    // Private-mode storage failures just mean an empty transcript.
  }
}
